import { useCallback, useMemo } from "react";
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
  type EntitySelection,
  type Omittable,
} from "@salestrak/ui";

import {
  TEAM_POOL_ID,
  teamToPoolItem,
  useTeamPool,
} from "#app/components/entity-selectors/hooks/use-team-pool";
import type { TeamChip } from "#app/lib/data";

// ---------------------------------------------------------------------------
// TeamsPickerContent — the Teams-property picker for card chips and table
// cells: applied teams pinned on top, the workspace pool below, applied per
// change. Mounts INLINE inside a caller-owned popover (SelectorPopover /
// DataTablePopoverCell), mirroring the property-chip selectors' grammar.
// ---------------------------------------------------------------------------

type TeamsPickerContentProps = Omittable<{
  /** Selected team ids. */
  value: string[];
  onValueChange: (ids: string[]) => void;
  /**
   * Already-resolved refs for the applied rows. Covers teams outside the
   * viewer's discoverable pool (a private team assigned to the issue still
   * renders its name — the by-reference contract).
   */
  selectedTeams?: TeamChip[];
}>;

export function TeamsPickerContent({
  value,
  onValueChange,
  selectedTeams,
}: TeamsPickerContentProps) {
  const { pool } = useTeamPool();

  // Applied rows resolve from the issue's own team refs first (covers
  // private teams outside the discoverable pool), then the pool.
  const selections = useMemo<EntitySelection[]>(() => {
    const itemMap = new Map(pool.items.map((item) => [item.id, item]));
    return value.map((teamId) => {
      const known = selectedTeams?.find((t) => t.teamId === teamId);
      const entity = known
        ? teamToPoolItem({
            teamId: known.teamId,
            teamName: known.teamName,
            icon: known.icon,
          })
        : (itemMap.get(teamId) ?? { id: teamId, title: "?" });
      return { id: teamId, poolId: TEAM_POOL_ID, entity };
    });
  }, [value, selectedTeams, pool.items]);

  // Applied teams STAY in the pool: the mounted Selected section omits them
  // itself and counts them as listed above, so a pool whose every team is
  // chosen shows the ticked rows and nothing else — never a note under them.
  // Pre-filtering them here defeated that guard.
  const config = useMemo(
    () => ({
      pools: [pool],
      searchPlaceholder: "Search teams...",
      emptyMessage: "No matching teams",
      noItemsMessage: "No teams yet",
    }),
    [pool],
  );

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange(next.map((s) => s.id));
    },
    [onValueChange],
  );

  return (
    <EntitySelector
      variant="inline"
      config={config}
      value={selections}
      onValueChange={handleValueChange}
    >
      <EntitySelectorContent>
        <EntitySelectorInput />
        <EntitySelectorSelected
          label="Teams"
          hideWhenEmpty
          hideBadge
          hideSelectionHighlight
        />
        <EntitySelectorPool
          value={TEAM_POOL_ID}
          label="All Teams"
          hideSelectionHighlight
        />
        <EntitySelectorEmpty>No matching teams</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}
